import { getTemplateTokenRegex } from '@rentguru/commons-utils';
import { type Editor, InputRule, Node, PasteRule } from '@tiptap/core';
import { DOMSerializer, type Schema } from '@tiptap/pm/model';
import { Plugin, PluginKey } from '@tiptap/pm/state';
import { ReactNodeViewRenderer } from '@tiptap/react';
import isNil from 'lodash/isNil';
import { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { TemplateTagNodeView } from '@ui/components/editor/TemplateTagNodeView';
import {
  COMPLETE_TEMPLATE_TOKEN_BEFORE_CARET_REGEX,
  isEveryTokenAvailable,
  readTemplateTagToken,
  TEMPLATE_TAG_LEAF_TEXT,
  TEMPLATE_TAG_NODE_NAME,
  TEMPLATE_TAG_TOKEN_ATTRIBUTE,
  TEMPLATE_TAG_WRAPPER_ELEMENT,
  TEMPLATE_TAG_WRAPPER_SELECTOR,
  type TemplateTagOption,
  wrapTemplateTokens,
} from '@ui/components/editor/templateTagUtils';
import { writeEmailSafeHtml } from '@ui/components/editor/templateTextUtils';

const TEMPLATE_TAG_PARSE_PRIORITY = 60;
const TEMPLATE_TAG_CLIPBOARD_PLUGIN_KEY = new PluginKey('templateTagClipboard');
const SCHEMA_DOM_SERIALIZER_CACHE_KEY = 'domSerializer';
const NO_TEMPLATE_TAGS: TemplateTagOption[] = [];

type SerializedContent = HTMLElement | DocumentFragment;

export interface TemplateTagConfiguration {
  tags: TemplateTagOption[];
  isTokenAvailable: (token: string) => boolean;
  unavailableTitle: string;
}

export interface TemplateTagStore {
  getConfiguration: () => TemplateTagConfiguration;
  configure: (configuration: TemplateTagConfiguration) => void;
  subscribe: (listener: () => void) => () => void;
}

export interface TemplateTagProps {
  tags?: TemplateTagOption[];
  isTokenAvailable?: (token: string) => boolean;
  unavailableTagTitle?: string;
}

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    templateTag: {
      insertTemplateTag: (token: string) => ReturnType;
    };
  }

  interface Storage {
    templateTag: TemplateTagStore;
  }
}

const DEFAULT_TEMPLATE_TAG_CONFIGURATION: TemplateTagConfiguration = {
  tags: [],
  isTokenAvailable: isEveryTokenAvailable,
  unavailableTitle: '',
};

const createTemplateTagStore = (): TemplateTagStore => {
  const listeners = new Set<() => void>();
  const state = { configuration: DEFAULT_TEMPLATE_TAG_CONFIGURATION };
  return {
    getConfiguration: () => state.configuration,
    configure: (configuration) => {
      state.configuration = configuration;
      listeners.forEach((listener) => listener());
    },
    subscribe: (listener) => {
      listeners.add(listener);
      return () => {
        listeners.delete(listener);
      };
    },
  };
};

export const configureTemplateTags = (editor: Editor, configuration: TemplateTagConfiguration) => {
  editor.storage.templateTag.configure(configuration);
};

export const useTemplateTagConfiguration = (
  editor: Editor | null,
  { tags = NO_TEMPLATE_TAGS, isTokenAvailable = isEveryTokenAvailable, unavailableTagTitle }: TemplateTagProps,
  enabled = true
) => {
  const { formatMessage } = useIntl();

  useEffect(() => {
    if (!enabled || isNil(editor) || editor.isDestroyed) {
      return;
    }
    configureTemplateTags(editor, {
      tags,
      isTokenAvailable,
      unavailableTitle: unavailableTagTitle ?? formatMessage({ id: 'richTextEditor.unavailableTemplateTag' }),
    });
  }, [editor, enabled, tags, isTokenAvailable, unavailableTagTitle, formatMessage]);
};

const unwrapTemplateTags = (content: SerializedContent): SerializedContent => {
  content.querySelectorAll(TEMPLATE_TAG_WRAPPER_SELECTOR).forEach((wrapper) => {
    wrapper.replaceWith(wrapper.textContent ?? '');
  });
  return content;
};

const toStoredHtml = (content: SerializedContent): SerializedContent => writeEmailSafeHtml(unwrapTemplateTags(content));

const createTemplateSerializer = (
  schema: Schema,
  finish: (content: SerializedContent) => SerializedContent
): DOMSerializer => {
  const serializer = new DOMSerializer(DOMSerializer.nodesFromSchema(schema), DOMSerializer.marksFromSchema(schema));
  const serializeFragment = serializer.serializeFragment.bind(serializer);
  serializer.serializeFragment = (fragment, options, target) => finish(serializeFragment(fragment, options, target));
  return serializer;
};

export const TemplateTag = Node.create({
  name: TEMPLATE_TAG_NODE_NAME,
  group: 'inline',
  inline: true,
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      token: {
        default: '',
        parseHTML: (element) => element.getAttribute(TEMPLATE_TAG_TOKEN_ATTRIBUTE) ?? '',
        renderHTML: () => ({}),
      },
    };
  },

  parseHTML() {
    return [{ tag: TEMPLATE_TAG_WRAPPER_SELECTOR, priority: TEMPLATE_TAG_PARSE_PRIORITY }];
  },

  renderHTML({ node }) {
    const token = readTemplateTagToken(node);
    return [TEMPLATE_TAG_WRAPPER_ELEMENT, { [TEMPLATE_TAG_TOKEN_ATTRIBUTE]: token }, token];
  },

  renderText({ node }) {
    return readTemplateTagToken(node);
  },

  addStorage() {
    return createTemplateTagStore();
  },

  onBeforeCreate() {
    const { schema } = this.editor;
    schema.cached[SCHEMA_DOM_SERIALIZER_CACHE_KEY] = createTemplateSerializer(schema, toStoredHtml);
  },

  addNodeView() {
    return ReactNodeViewRenderer(TemplateTagNodeView);
  },

  addCommands() {
    return {
      insertTemplateTag:
        (token) =>
        ({ tr, dispatch }) => {
          if (dispatch) {
            tr.replaceSelectionWith(this.type.create({ token }), true).scrollIntoView();
          }
          return true;
        },
    };
  },

  addInputRules() {
    return [
      new InputRule({
        find: COMPLETE_TEMPLATE_TOKEN_BEFORE_CARET_REGEX,
        handler: ({ state, range, match }) => {
          const [token] = match;
          const typedText = state.doc.textBetween(range.from, range.to, undefined, TEMPLATE_TAG_LEAF_TEXT);
          if (!token.startsWith(typedText) || typedText.length >= token.length) {
            return null;
          }
          const marks = state.doc.nodeAt(range.from)?.marks;
          state.tr.replaceWith(range.from, range.to, this.type.create({ token }, null, marks));
          return undefined;
        },
      }),
    ];
  },

  addPasteRules() {
    return [
      new PasteRule({
        find: getTemplateTokenRegex(),
        handler: ({ state, range, match }) => {
          const [token] = match;
          if (state.doc.textBetween(range.from, range.to, undefined, TEMPLATE_TAG_LEAF_TEXT) !== token) {
            return;
          }
          const marks = state.doc.nodeAt(range.from)?.marks;
          state.tr.replaceWith(range.from, range.to, this.type.create({ token }, null, marks));
        },
      }),
    ];
  },

  addProseMirrorPlugins() {
    return [
      new Plugin({
        key: TEMPLATE_TAG_CLIPBOARD_PLUGIN_KEY,
        props: {
          transformPastedHTML: (html) => wrapTemplateTokens(html),
          clipboardSerializer: createTemplateSerializer(this.editor.schema, unwrapTemplateTags),
        },
      }),
    ];
  },
});
